import { LoadingButton } from '@mui/lab'
import {
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { IContract } from '../../definitions/Contract'
import Helper from '../../helpers/Helper'
import ContractChangesModal from '../editor/form_builder/contract_editor/ContractChangesModal'
import useQuery, { buildQueryKey } from '../hooks/useQuery'

interface P {
    contractData: IContract
    open: boolean
    onClose: () => void
}

const ignoredFields = [
    'id',
    'uuid',
    '_signature',
    'createdAt',
    'lastUpdate',
    'ancestorKey',
    'versioning'
]

const ContractCompareModal = ({ open, onClose, contractData }: P) => {
    const [changesOpen, setChangesOpen] = useState(false)
    const [differences, setDifferences] = useState<any[]>([])
    const [loadingCompare, setLoadingCompare] = useState(false)
    const [selectedContract, setSelectedContract] = useState<Pick<IContract, 'uuid' | 'name' | 'id' | 'versioning'> | null>(null)
    const [selectedVersion, setSelectedVersion] = useState(null)
    console.log('[debug] ~ ContractCompareModal ~ differences:', differences)

    const { response: contractsQuery } = useQuery({
        queryKey: buildQueryKey('getHeadContractNames'),
        queryFn: () => ApiClient.getHeadContractNames(),
        cache: true
    })

    const { status: allVersionsStatus, response: allContractVersions } = useQuery({
        queryKey: buildQueryKey('getSettlementsByContractId' + selectedContract?.uuid),
        queryFn: () => ApiClient.getContractVersions(selectedContract?.versioning?.ancestorKey),
        cache: false,
        enabled: !!selectedContract
    })

    const sortedVersions = [...(allContractVersions ?? [])].sort((a, b) => {
        return b.versioning.version - a.versioning.version
    })

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
                <DialogTitle>Select Contract & Version</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {/* Contract Autocomplete */}
                    <Autocomplete
                        options={contractsQuery ?? []}
                        getOptionLabel={option => option.name}
                        value={selectedContract}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, value) => {
                            setSelectedContract(value)
                            setSelectedVersion(null) // reset version
                        }}
                        renderInput={params => (
                            <TextField {...params} label='Contract' />
                        )}
                    />

                    {/* Version Autocomplete */}
                    <Autocomplete
                        options={sortedVersions ?? []}
                        getOptionLabel={option => option.versioning.version.toLocaleString()}
                        value={selectedVersion}
                        onChange={(_, value) => setSelectedVersion(value)}
                        disabled={!selectedContract || allVersionsStatus !== 'success'}
                        loading={allVersionsStatus === 'loading'}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label='Version'
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {allVersionsStatus === 'loading' && <CircularProgress size={20} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                    />

                </DialogContent>

                <DialogActions>
                    <LoadingButton disabled={loadingCompare} onClick={onClose}>Cancel</LoadingButton>
                    <LoadingButton
                        loading={loadingCompare}
                        onClick={async () => {
                            if (!selectedVersion?.uuid) return
                            try {
                                setLoadingCompare(true)
                                const selectedContractFull = await ApiClient.getContract(selectedVersion.uuid)
                                const currentContract = contractData
                                const diffs = Helper.compareItem(
                                    currentContract,
                                    selectedContractFull
                                )

                                const filteredDiffs = diffs.filter(d =>
                                    !ignoredFields.some(field => d.path.includes(field))
                                )
                                setDifferences(filteredDiffs)
                                setChangesOpen(true)
                            } finally {
                                setLoadingCompare(false)
                            }
                        }}
                        disabled={!selectedContract || !selectedVersion}
                        variant='contained'>
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <ContractChangesModal
                open={changesOpen}
                onClose={() => setChangesOpen(false)}
                differences={differences}
            />
        </>

    )
}

export default ContractCompareModal
